import React, { useCallback, useContext, useMemo, useState } from 'react'
import { ALL_CATEGORIES, BASE_CATEGORIES, DEFAULT_SUBMODEL, NO_CATEGORIES_AVAILABLE_MESSAGE, NO_CATEGORY_FOUND_SEARCH_MESSAGE, SHOW_ONLY_OVERRIDDEN, SHOW_ONLY_OVERRIDDEN_MESSAGE, VIEW_LESS, VIEW_MORE } from '../ARSConstants'
import { ArsContext } from '../Contexts/ArsContext'
import { SearchIcon, UpArrow, PlainDownArrow } from 'assests/GmsIcons'
import CategoryItem from './CategoryItem';
import OverlayIcon from 'Components/GMS/Rules/Components/OverlayIcon';
import { TSubmodel } from '../Types';

type DefaultSubmodelBaseSelectorProps = {
    submodel: TSubmodel
}

const DefaultSubmodelBaseSelector = ({ submodel }: DefaultSubmodelBaseSelectorProps) => {
    const [searchString, setSearchString] = useState<string>("");
    const [selectedCategoryTypeId, setSelectedCategoryTypeId] = useState<string>("");
    const [viewMoreCategories, setViewMoreCategories] = useState<boolean>(false);
    const [showOnlyOverridden, setShowOnlyOverridden] = useState<boolean>(false);
    const [expanded, setExpanded] = useState<boolean>(true);
    const { model, setModel, allCategories, allCategoryTypes, numberOfCategoriesToShow } = useContext(ArsContext);

    const isDefaultSubmodel = submodel?.name === DEFAULT_SUBMODEL;
    const filterCategories = (categoryTypeId: string) => {
        setSelectedCategoryTypeId(categoryTypeId);
    };

    const handleSearch = (value: string) => {
        setSearchString(value);
    }

    const isCategorySelectedInSubmodels = useCallback((categoryId: string): boolean => {
        if (!model?.submodel) return false;

        return model.submodel.some(submodel => {
            if (submodel.name === DEFAULT_SUBMODEL) {
                return false;
            }
            return submodel?.categories && submodel?.categories.includes(categoryId);
        });
    }, [model?.submodel]);

    const filteredCategories = useMemo(() => {
        if (!allCategories?.length) return [];

        const searchLower = searchString.toLowerCase();

        return allCategories.filter(category => {
            if (selectedCategoryTypeId && category.cattype !== selectedCategoryTypeId) {
                return false;
            }

            if (showOnlyOverridden) {
                const isOverriddenCategory = isCategorySelectedInSubmodels(category.id);
                if (!isOverriddenCategory) {
                    return false;
                }
            }

            // Filter by search string
            if (searchString) {
                const name = category.name?.toLowerCase() || '';
                const description = category.description?.toLowerCase() || '';
                if (!name.includes(searchLower) && !description.includes(searchLower)) {
                    return false;
                }
            }

            return true;
        });
    }, [
        allCategories,
        selectedCategoryTypeId,
        searchString,
        showOnlyOverridden,
        isCategorySelectedInSubmodels
    ]);

    const toggleCategory = (categoryId: string) => {
        if (!model || !submodel) return;

        setModel(prevModel => {
            if (!prevModel) return null;
            const updatedModel = { ...prevModel };
            const submodelIndex = updatedModel.submodel.findIndex(
                sm => sm.submodel_id === submodel.submodel_id
            );

            if (submodelIndex === -1) return prevModel;
            const currentSubmodel = { ...updatedModel.submodel[submodelIndex] };
            const categoryArray = currentSubmodel.categories ? [...currentSubmodel.categories] : [];
            if (categoryArray.includes(categoryId)) {
                currentSubmodel.categories = categoryArray.filter(id => id !== categoryId);
            } else {
                currentSubmodel.categories = [...categoryArray, categoryId];
            }
            updatedModel.submodel[submodelIndex] = currentSubmodel;
            return updatedModel;
        });
    };

    const toggleShowOnlyOverridden = (e: React.ChangeEvent<HTMLInputElement>) => {
        setShowOnlyOverridden(e.target.checked)
        setSearchString("")
        setSelectedCategoryTypeId("")
    }

    return (
        <div className="tw-w-full tw-ps-2 tw-pt-2">
            <div>
                <div className="tw-flex tw-items-center tw-justify-between tw-w-full tw-p-4 tw-text-left tw-bg-white tw-rounded-t tw-border tw-border-[#c9cccf]">
                    <span className="tw-text-lg tw-font-semibold">{BASE_CATEGORIES}</span>
                    <div role="button" data-testid="components-defaultsubmodelbaseselector-div-1" className='tw-p-1 tw-cursor-pointer' onClick={() => { setExpanded((prev) => !prev) }}>{expanded ? <UpArrow /> : <PlainDownArrow />}</div>
                </div>
                {expanded ? <div className=" tw-h-auto tw-bg-[#f4f5f7] tw-rounded-bl tw-rounded-br ">
                    <div className="tw-p-4 tw-bg-gray-50 tw-border tw-border-[#c9cccf]">
                        <div className="tw-mb-3 tw-flex tw-justify-between tw-items-center">
                            <span className="tw-text-lg tw-font-medium">{ALL_CATEGORIES}</span>
                            <div className="tw-flex tw-space-x-2">
                                < div className="tw-flex tw-items-center tw-gap-2">
                                    <input data-testid="components-defaultsubmodelbaseselector-input-1"
                                        type="checkbox"
                                        id="showOverridden"
                                        className="tw-h-4 tw-w-4 tw-rounded tw-border-gray-300 tw-text-blue-600 focus:tw-ring-accent-hover"
                                        onChange={toggleShowOnlyOverridden}
                                        checked={showOnlyOverridden}
                                    />
                                    <label htmlFor="showOverridden" className="tw-justify-start tw-text-sm tw-text-slate-500">
                                        {SHOW_ONLY_OVERRIDDEN}
                                    </label>
                                    <OverlayIcon
                                        message={SHOW_ONLY_OVERRIDDEN_MESSAGE}
                                        placement="bottom"
                                    />
                                </div>
                                <select data-testid="components-defaultsubmodelbaseselector-select-2"
                                    className="tw-w-48 tw-max-w-48 tw-px-2 tw-h-8 tw-bg-gray-100 tw-rounded tw-border tw-border-[#c9c9cf] tw-text-ellipsis tw-overflow-hidden"
                                    onChange={(e) => filterCategories(e.target.value)}
                                    value={selectedCategoryTypeId}
                                >
                                    <option value="">Select category type</option>
                                    {allCategoryTypes?.map((item) => (
                                        <option key={item.id} value={item.id}>
                                            {item.name}
                                        </option>
                                    ))}
                                </select>
                                <div className="tw-relative">
                                    <input data-testid="components-defaultsubmodelbaseselector-input-3"
                                        type="text"
                                        className="tw-px-2 tw-h-8 tw-bg-white tw-rounded tw-border tw-border-[#c9c9cf]"
                                        placeholder="Search Category"
                                        value={searchString}
                                        onChange={(e) => handleSearch(e.target.value)}
                                    />
                                    <span
                                        className="tw-absolute tw-inset-y-0 tw-right-0 tw-px-3 tw-py-1.5 tw-flex tw-items-center tw-justify-center tw-text-gray-400"
                                        style={{ pointerEvents: "none" }}
                                    >
                                        <SearchIcon />
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="tw-flex tw-flex-wrap">
                            {filteredCategories && filteredCategories.length > 0 ? (
                                filteredCategories
                                    .slice(
                                        0,
                                        viewMoreCategories || searchString !== ""
                                            ? filteredCategories.length
                                            : Math.min(numberOfCategoriesToShow, filteredCategories.length)
                                    )
                                    .map((item: TCategory) => {
                                        return <CategoryItem key={item?.id} item={item} isSelected={false} handleClick={toggleCategory} isOverridden={isDefaultSubmodel && isCategorySelectedInSubmodels(item?.id)} disabledClick={submodel?.name === DEFAULT_SUBMODEL} />
                                    })
                            ) : searchString !== "" ? (
                                <div className="tw-text-gray-500">
                                    {NO_CATEGORY_FOUND_SEARCH_MESSAGE}
                                </div>
                            ) : (
                                <div className="tw-text-gray-500">{NO_CATEGORIES_AVAILABLE_MESSAGE}</div>
                            )}

                            {searchString === "" &&
                                filteredCategories?.length > numberOfCategoriesToShow && (
                                    <button data-testid="components-defaultsubmodelbaseselector-button-4"
                                        onClick={() => setViewMoreCategories(!viewMoreCategories)}
                                        className="tw-text-blue-500 hover:tw-text-blue-700 tw-ml-2"
                                    >
                                        {viewMoreCategories ? VIEW_LESS : VIEW_MORE}
                                    </button>
                                )}
                        </div>
                    </div>
                </div> : null}
            </div>
        </div >
    );
}

export default DefaultSubmodelBaseSelector